import React from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import {
  Grid,
  Modal,
  Autocomplete,
  Tooltip,
  Icon,
  Avatar,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import FormField from "components/FormField";
import { useSnackbar } from "notistack";
import { EMAIL_REGEX } from "helpers/regexs";
import { useGetGruposQuery } from "services/administracion/grupos.service";
import { useMaterialUIController } from "context";

import form from "../../pages/new-registro/schemas/form";
import initialValues from "../../pages/new-registro/schemas/initialValues";
import validations from "../../pages/new-registro/schemas/validations";

import { usePlantillaFetch, usePlantillaSubmit } from "./hooks";
import Telefonos from "examples/TelefonosCRUD/telefonos";
import pxToRem from "assets/theme/functions/pxToRem";

const NewPlantilla = ({ open, handleClose, id }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { formId, formField } = form;
  const { nombre, apellido_paterno, apellido_materno, correo, foto, groups } =
    formField;

  const { data: grupos } = useGetGruposQuery(
    {
      filter_params: {
        query: "{name, url, id}",
      },
      shape: {
        id: "id",
        name: "name",
        url: "url",
      },
    },
    {
      refetchOnReconnect: true,
    },
  );

  const { enqueueSnackbar } = useSnackbar();

  const currentValidation = validations[0]; // validations es un array en el schema original

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 900,
    boxShadow: 24,
    borderRadius: "12px",
    px: "40px",
    py: "29px",
    outline: "none",
    maxHeight: "95vh",
    overflowY: "auto",
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <MDBox sx={modalStyle} bgColor={darkMode ? "black" : "white"}>
        <Formik
          initialValues={initialValues}
          validationSchema={currentValidation}
          onSubmit={() => {}}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
            setErrors,
            setSubmitting,
            resetForm,
          }) => {
            const { oldValues } = usePlantillaFetch(
              id,
              setFieldValue,
              formField,
            );

            const { handleSubmit } = usePlantillaSubmit(
              id,
              handleClose,
              oldValues,
            );

            const handleChangeFoto = (e) => {
              const file = e.target.files[0];
              const maxSize = 1024 * 1024; // 1 MB

              if (file.size > maxSize) {
                enqueueSnackbar(
                  "El tamaño de la imagen no debe ser mayor a 1 MB",
                  {
                    variant: "error",
                    autoHideDuration: 4000,
                  },
                );
                return;
              } else {
                setFieldValue(foto.name, e.target.files[0]);
              }
            };

            return (
              <Form
                id={formId}
                autoComplete="off"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit(values, { setSubmitting, resetForm, setErrors });
                }}
              >
                <Grid container columnSpacing={2}>
                  <Grid item xs={12} display="flex" alignItems="flex-start">
                    <Grid container columnSpacing={2}>
                      <Grid
                        item
                        xs={12}
                        sm={3}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Tooltip
                          placement="top"
                          title={
                            !values.foto ? "Agregar imagen" : "Cambiar Foto"
                          }
                        >
                          <MDBox position="relative" display="inline-flex">
                            <input
                              accept="image/*"
                              style={{ display: "none" }}
                              id="student-image-upload"
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  if (file.size > 1024 * 1024) {
                                    enqueueSnackbar(
                                      "La imagen debe ser menor a 1MB",
                                      { variant: "error" },
                                    );
                                  } else {
                                    setFieldValue("foto", file);
                                  }
                                }
                              }}
                            />
                            <label
                              htmlFor="student-image-upload"
                              style={{ cursor: "pointer" }}
                            >
                              <Avatar
                                src={
                                  values.foto
                                    ? typeof values.foto === "string"
                                      ? values.foto
                                      : URL.createObjectURL(values.foto)
                                    : undefined
                                }
                                sx={{
                                  width: 150,
                                  height: 150,
                                  bgcolor: values.foto
                                    ? "transparent"
                                    : "#e0e0e0",
                                }}
                              >
                                <Icon
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    fontSize: "60px !important",
                                    color: "#9e9e9e",
                                  }}
                                >
                                  person
                                </Icon>
                              </Avatar>
                            </label>

                            <MDBox
                              component="label"
                              htmlFor="student-image-upload"
                              position="absolute"
                              bottom={0}
                              right={10}
                              sx={{
                                bgcolor: "#3784A1",
                                borderRadius: "50%",
                                p: 1,
                                cursor: "pointer",
                                border: "2px solid white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                zIndex: 2,
                              }}
                            >
                              <Icon sx={{ color: "white" }}>camera_alt</Icon>
                            </MDBox>
                          </MDBox>
                        </Tooltip>
                      </Grid>

                      <Grid item xs={12} sm={9}>
                        <MDBox mb={1}>
                          <FormField
                            type={nombre.type}
                            label={nombre.label}
                            name={nombre.name}
                            value={values.nombre}
                            placeholder={nombre.placeholder}
                            error={errors.nombre && touched.nombre}
                            success={
                              values.nombre?.length > 0 && !errors.nombre
                            }
                            onInput={(e) =>
                              (e.target.value = (
                                "" + e.target.value
                              ).toUpperCase())
                            }
                          />
                        </MDBox>
                        <MDBox mb={1}>
                          <FormField
                            type={apellido_paterno.type}
                            label={apellido_paterno.label}
                            name={apellido_paterno.name}
                            value={values.apellido_paterno}
                            placeholder={apellido_paterno.placeholder}
                            error={
                              errors.apellido_paterno &&
                              touched.apellido_paterno
                            }
                            success={
                              values.apellido_paterno?.length > 0 &&
                              !errors.apellido_paterno
                            }
                            onInput={(e) =>
                              (e.target.value = (
                                "" + e.target.value
                              ).toUpperCase())
                            }
                          />
                        </MDBox>
                        <MDBox mb={1}>
                          <FormField
                            type={apellido_materno.type}
                            label={apellido_materno.label}
                            name={apellido_materno.name}
                            value={values.apellido_materno}
                            placeholder={apellido_materno.placeholder}
                            error={
                              errors.apellido_materno &&
                              touched.apellido_materno
                            }
                            success={
                              values.apellido_materno?.length > 0 &&
                              !errors.apellido_materno
                            }
                            onInput={(e) =>
                              (e.target.value = (
                                "" + e.target.value
                              ).toUpperCase())
                            }
                          />
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <Autocomplete
                    multiple
                    id="distribuir-rol-plantilla"
                    options={grupos ?? []}
                    onChange={(event, val) => {
                      setFieldValue(
                        groups.name,
                        val?.map((option) => option.id),
                      );
                    }}
                    value={
                      grupos?.filter((option) =>
                        values.groups?.includes(option.id),
                      ) || []
                    }
                    isOptionEqualToValue={(option, value) => {
                      return `${option.name}` === `${value.name}`;
                    }}
                    getOptionLabel={(option) => `${option.name}`}
                    renderInput={(params) => (
                      <FormField
                        id="distribucionRol"
                        name={"distribucionRol"}
                        label="Asignar rol/roles"
                        {...params}
                        error={errors.groups && touched.groups}
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        padding: "0px !important",
                        paddingLeft: `${pxToRem(6)} !important`,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} mt={2}>
                  <Telefonos
                    formData={{
                      values,
                      touched,
                      formField,
                      errors,
                      setErrors,
                      setFieldValue,
                      handleChange,
                    }}
                  />
                </Grid>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Icon
                      sx={{
                        color: "#3784A1",
                        fontSize: "2rem !important", // Make it slightly larger as per design usually
                      }}
                    >
                      email
                    </Icon>
                  </Grid>
                  <Grid item xs>
                    <FormField
                      type={correo.type}
                      name={correo.name}
                      value={values.correo}
                      placeholder={correo.placeholder}
                      error={errors.correo && touched.correo}
                      success={
                        !!values.correo?.match(EMAIL_REGEX) && !errors.correo
                      }
                    />
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="center"
                  gap={2}
                  mt={2}
                >
                  <MDButton
                    variant="contained"
                    color="accent"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    Aceptar
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="accent"
                    onClick={handleClose}
                  >
                    Cancelar
                  </MDButton>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </MDBox>
    </Modal>
  );
};

NewPlantilla.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  id: PropTypes.string,
};

export default NewPlantilla;
